import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, money, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { JourneysTab } from './Journeys.tsx';
import { Bars, downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * Marketing: e-mails to the customers who agreed to hear from the business. Campaigns are
 * written from a few blocks with the e-mail shown beside them as it will look, go to an
 * audience (people who fit some rules, or a hand-made list), and go out through the business's
 * own mail server. Afterwards the page shows who opened, clicked and asked for no more.
 */

type Tab = 'overview' | 'campaigns' | 'journeys' | 'audiences' | 'contacts' | 'settings';
type CState = 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled';

type Block =
  | { type: 'heading'; text: string }
  | { type: 'text'; text: string }
  | { type: 'button'; label: string; url: string }
  | { type: 'image'; url: string; alt: string; link: string | null }
  | { type: 'products'; ids: string[]; url: string | null }
  | { type: 'offer'; title: string; code: string | null; note: string | null }
  | { type: 'divider' };
type BlockType = Block['type'];

interface Row {
  id: string;
  number: number;
  name: string;
  subject: string;
  state: CState;
  scheduled_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  recipients: number;
  sent: number;
  failed: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  audience_name: string | null;
}
interface Campaign extends Row {
  preheader: string | null;
  blocks: Block[];
  audience: string | null;
  links: string[];
  last_error: string | null;
  sent_by_name: string | null;
  tested_at: string | null;
  audience_count: Count | null;
  link_stats: { url: string; clicks: number; people: number }[];
  opens_by_hour: { hour: string; n: number }[];
  queued: number;
  ab_percent: number;
  ab_subject: string | null;
  ab_preheader: string | null;
  ab_hours: number;
  ab_metric: AbMetric;
  ab_winner: 'a' | 'b' | null;
  ab_decided_at: string | null;
  ab: { a: AbSide; b: AbSide; held: number; decide_at: string | null } | null;
}
type AbMetric = 'opens' | 'clicks';
interface AbSide {
  sent: number;
  opened: number;
  clicked: number;
  open_rate: number;
  click_rate: number;
}
const AB_PERCENTS = [10, 20, 30, 40, 50];
const AB_HOURS = [1, 2, 4, 8, 12, 24, 48];
interface Count {
  total: number;
  reachable: number;
  no_email: number;
  not_agreed: number;
  opted_out: number;
}
interface Rules {
  who: 'all' | 'people' | 'companies';
  vip: boolean;
  towns: string[];
  bought_within: number | null;
  away_for: number | null;
  spent_at_least: number | null;
  birthday_month: number | null;
  new_within: number | null;
}
interface AudienceRow extends Count {
  id: string;
  name: string;
  kind: 'rules' | 'list';
  rules: Rules;
  description: string | null;
  active: boolean;
  campaigns: number;
  last_sent: string | null;
}
interface Overview {
  ready: boolean;
  missing: string[];
  last_error: string | null;
  contacts: {
    customers: number;
    with_email: number;
    reachable: number;
    agreed_30d: number;
    opted_out_30d: number;
  };
  last90: {
    campaigns: number;
    sent: number;
    opened: number;
    clicked: number;
    unsubscribed: number;
    open_rate: number;
    click_rate: number;
  };
  recent: Row[];
  queued: number;
  per_minute: number;
}
interface Setup {
  ready: boolean;
  from: string | null;
  audiences: {
    id: string;
    name: string;
    kind: 'rules' | 'list';
    reachable: number;
  }[];
  products: { id: string; name: string; price: string }[];
  towns: string[];
  sales: boolean;
}
interface Contact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  town: string | null;
  is_company: boolean;
  marketing_at: string | null;
  reachable: boolean;
  opted_out_at: string | null;
  received: number;
  opened: number;
  last_opened: string | null;
}

const TABS: { key: Tab; icon: IconName; perm?: string }[] = [
  { key: 'overview', icon: 'chart' },
  { key: 'campaigns', icon: 'send' },
  { key: 'journeys', icon: 'repeat' },
  { key: 'audiences', icon: 'users' },
  { key: 'contacts', icon: 'list' },
  { key: 'settings', icon: 'settings', perm: 'marketing.settings' },
];
const STATE: Record<CState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  scheduled: { tone: 'info', icon: 'clock' },
  sending: { tone: 'warn', icon: 'send' },
  sent: { tone: 'ok', icon: 'checkCircle' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const campName = (n: number) => `M-${n}`;
const pct = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 1000) / 10}%` : '—');
const blankRules = (): Rules => ({
  who: 'all',
  vip: false,
  towns: [],
  bought_within: null,
  away_for: null,
  spent_at_least: null,
  birthday_month: null,
  new_within: null,
});

function CampaignBadge({ c }: { c: Pick<Row, 'state'> }) {
  const s = STATE[c.state];
  return <StatusBadge tone={s.tone} icon={s.icon} label={t(`mkt.st_${c.state}` as MessageKey)} />;
}

export function Marketing() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const tabs = TABS.filter((x) => !x.perm || can(x.perm));
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'overview') as Tab;
  const { data: o, reload } = useAction<Overview>('marketing.overview');
  const [version, setVersion] = useState(0);
  const [edit, setEdit] = useState<string | 'new' | null>(null);
  const open = params.get('c');
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  const manage = can('marketing.manage');

  return (
    <Shell>
      <div className="page reports mkt-page">
        <div className="page-head">
          <div>
            <h1>{t('mkt.title')}</h1>
            <p className="muted">{t('mkt.subtitle')}</p>
          </div>
          {manage ? (
            <div className="page-actions">
              <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
                {t('mkt.new')}
              </Button>
            </div>
          ) : null}
        </div>
        {o && !o.ready ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>
              {t('mkt.not_ready')}{' '}
              {can('marketing.settings') ? (
                <button className="link" onClick={() => set({ tab: 'settings' })}>
                  {t('mkt.set_up')}
                </button>
              ) : (
                t('mkt.ask_manager')
              )}
            </span>
          </p>
        ) : o?.last_error ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t('mkt.server_error', { error: o.last_error })}</span>
          </p>
        ) : null}
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('mkt.at_a_glance')}>
            <Kpi
              label={t('mkt.k_reachable')}
              value={String(o.contacts.reachable)}
              hint={t('mkt.k_reachable_hint', {
                n: o.contacts.with_email,
                all: o.contacts.customers,
              })}
            />
            <Kpi label={t('mkt.k_open_rate')} value={o.last90.sent ? `${o.last90.open_rate}%` : '—'} hint={tn('mkt.k_sent_90', o.last90.sent)} />
            <Kpi label={t('mkt.k_click_rate')} value={o.last90.sent ? `${o.last90.click_rate}%` : '—'} hint={tn('mkt.k_clicked', o.last90.clicked)} />
            <Kpi
              label={t('mkt.k_growth')}
              value={`+${o.contacts.agreed_30d}`}
              hint={o.contacts.opted_out_30d ? tn('mkt.k_out', o.contacts.opted_out_30d) : t('mkt.k_nobody_out')}
              warn={o.contacts.opted_out_30d > o.contacts.agreed_30d}
            />
          </section>
        )}
        <div className="segmented tabs mkt-tabs" role="tablist" aria-label={t('mkt.sections')}>
          {tabs.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'overview' ? null : x.key, f: null })}>
              <Icon name={x.icon} size={16} />
              {t(`mkt.tab_${x.key}` as MessageKey)}
            </button>
          ))}
        </div>
        {!o ? null : tab === 'overview' ? (
          <OverviewTab o={o} onOpen={(id) => set({ c: id })} onNew={manage ? () => setEdit('new') : undefined} />
        ) : tab === 'campaigns' ? (
          <CampaignsTab key={version} params={params} set={set} onOpen={(id) => set({ c: id })} />
        ) : tab === 'journeys' ? (
          <JourneysTab key={version} params={params} set={set} />
        ) : tab === 'audiences' ? (
          <AudiencesTab key={version} onChange={changed} />
        ) : tab === 'contacts' ? (
          <ContactsTab key={version} params={params} set={set} onChange={changed} />
        ) : (
          <SettingsTab onChange={changed} />
        )}
      </div>
      {open ? <CampaignDialog key={`${open}-${version}`} id={open} onClose={() => set({ c: null })} onChange={changed} onEdit={(id) => setEdit(id)} onOpen={(id) => set({ c: id })} /> : null}
      {edit ? (
        <Editor
          id={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={async (id) => {
            setEdit(null);
            await changed();
            set({ c: id });
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- overview ---------------------------------------------------------------------------------------

function OverviewTab({ o, onOpen, onNew }: { o: Overview; onOpen: (id: string) => void; onNew?: () => void }) {
  return (
    <div className="shop-overview">
      <section className="panel">
        <div className="prot-history-head">
          <div>
            <h2 className="rep-sub">{t('mkt.recent')}</h2>
            <p className="muted small">{t('mkt.recent_hint')}</p>
          </div>
        </div>
        {o.recent.length ? (
          <ul className="mkt-recent">
            {o.recent.map((c) => (
              <li key={c.id}>
                <button className="mkt-recent-row" onClick={() => onOpen(c.id)}>
                  <span className="mkt-recent-main">
                    <strong>{c.name}</strong>
                    <span className="muted small">
                      {[c.audience_name, c.state === 'scheduled' && c.scheduled_at ? t('mkt.goes_at', { at: dateTime(c.scheduled_at) }) : c.started_at ? dateTime(c.started_at) : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  {c.state === 'sent' || c.state === 'sending' ? (
                    <span className="mkt-rates">
                      <Rate label={t('mkt.opened')} value={pct(c.opened, c.sent)} />
                      <Rate label={t('mkt.clicked')} value={pct(c.clicked, c.sent)} />
                    </span>
                  ) : (
                    <CampaignBadge c={c} />
                  )}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon="send"
            title={t('mkt.none_yet')}
            body={t('mkt.none_yet_hint')}
            action={
              onNew ? (
                <Button icon="plus" onClick={onNew}>
                  {t('mkt.new')}
                </Button>
              ) : undefined
            }
          />
        )}
      </section>
      <div className="shop-side">
        <section className="panel">
          <h2 className="rep-sub">{t('mkt.last_90')}</h2>
          <dl className="sub-moves">
            <div>
              <dt>{t('mkt.campaigns_sent')}</dt>
              <dd className="tabular">{o.last90.campaigns}</dd>
            </div>
            <div>
              <dt>{t('mkt.emails_sent')}</dt>
              <dd className="tabular">{o.last90.sent}</dd>
            </div>
            <div>
              <dt>{t('mkt.opened')}</dt>
              <dd className="tabular">
                {o.last90.opened} · {o.last90.sent ? `${o.last90.open_rate}%` : '—'}
              </dd>
            </div>
            <div>
              <dt>{t('mkt.clicked')}</dt>
              <dd className="tabular">
                {o.last90.clicked} · {o.last90.sent ? `${o.last90.click_rate}%` : '—'}
              </dd>
            </div>
            <div>
              <dt>{t('mkt.unsubscribed')}</dt>
              <dd className="tabular">{o.last90.unsubscribed}</dd>
            </div>
          </dl>
          {o.queued ? <p className="muted small">{t('mkt.queued', { n: o.queued, per: o.per_minute })}</p> : null}
        </section>
        <section className="panel">
          <h2 className="rep-sub">{t('mkt.how_it_works')}</h2>
          <ul className="mkt-steps">
            <li>
              <Icon name="users" size={16} />
              <span>{t('mkt.how_1')}</span>
            </li>
            <li>
              <Icon name="edit" size={16} />
              <span>{t('mkt.how_2')}</span>
            </li>
            <li>
              <Icon name="send" size={16} />
              <span>{t('mkt.how_3')}</span>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}

function Rate({ label, value }: { label: string; value: string }) {
  return (
    <span className="mkt-rate">
      <strong className="tabular">{value}</strong>
      <span className="muted small">{label}</span>
    </span>
  );
}

// ---- campaigns ----------------------------------------------------------------------------------------

const C_FILTERS = ['all', 'draft', 'scheduled', 'sent'] as const;

function CampaignsTab({ params, set, onOpen }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void; onOpen: (id: string) => void }) {
  const filter = (C_FILTERS as readonly string[]).includes(params.get('f') ?? '') ? (params.get('f') as (typeof C_FILTERS)[number]) : 'all';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const { data } = useAction<Row[]>('marketing.campaigns', {
    filter,
    ...(q ? { search: q } : {}),
  });
  const csv = () =>
    data &&
    downloadCsv(
      'campaigns.csv',
      [t('mkt.c_campaign'), t('mkt.subject'), t('mkt.audience'), t('mkt.c_when'), t('mkt.c_sent'), t('mkt.opened'), t('mkt.clicked'), t('mkt.unsubscribed'), t('mkt.c_state')],
      data.map((c) => [
        `${campName(c.number)} ${c.name}`,
        c.subject,
        c.audience_name ?? '',
        c.started_at ?? c.scheduled_at ?? '',
        c.sent,
        c.opened,
        c.clicked,
        c.unsubscribed,
        t(`mkt.st_${c.state}` as MessageKey),
      ]),
    );
  return (
    <section className="panel">
      <div className="toolbar">
        <div className="segmented subtle mkt-filters" role="radiogroup" aria-label={t('mkt.show')}>
          {C_FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => set({ f: f === 'all' ? null : f })}>
              {t(`mkt.f_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <span className="row-actions">
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" value={search} placeholder={t('mkt.search')} aria-label={t('mkt.search')} onChange={(e) => setSearch(e.target.value)} />
          </label>
          <IconButton icon="download" label={t('mkt.csv')} onClick={csv} disabled={!data?.length} />
        </span>
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : (
        <Table
          view="marketing.campaigns"
          head={[t('mkt.c_campaign'), t('mkt.audience'), t('mkt.c_when'), t('mkt.c_sent'), t('mkt.opened'), t('mkt.clicked'), t('mkt.c_state')]}
          phoneHide={[1, 2, 3, 5]}
          left={[0, 1, 2]}
          empty={t('mkt.none_found')}
          rows={data.map((c) => ({
            key: c.id,
            onClick: () => onOpen(c.id),
            cells: [
              <span className="vat-party" key="n">
                <span>{c.name}</span>
                <span className="muted">
                  {campName(c.number)} · {c.subject}
                </span>
              </span>,
              c.audience_name ?? <span className="muted">—</span>,
              <span key="w" className="tabular">
                {c.started_at ? dateTime(c.started_at) : c.scheduled_at ? dateTime(c.scheduled_at) : <span className="muted">—</span>}
              </span>,
              <span key="s" className="tabular">
                {c.state === 'draft' || c.state === 'scheduled' ? '—' : c.sent}
              </span>,
              <span key="o" className="tabular">
                {c.sent ? pct(c.opened, c.sent) : '—'}
              </span>,
              <span key="k" className="tabular">
                {c.sent ? pct(c.clicked, c.sent) : '—'}
              </span>,
              <CampaignBadge key="st" c={c} />,
            ],
          }))}
        />
      )}
    </section>
  );
}

const R_FILTERS = ['all', 'opened', 'clicked', 'not_opened', 'unsubscribed', 'failed'] as const;

function CampaignDialog({ id, onClose, onChange, onEdit, onOpen }: { id: string; onClose: () => void; onChange: () => Promise<void>; onEdit: (id: string) => void; onOpen: (id: string) => void }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const { data: c, reload } = useAction<Campaign>('marketing.campaign', { id });
  const [view, setView] = useState<'results' | 'email'>('results');
  const [ask, setAsk] = useState<null | 'send' | 'test' | 'delete'>(null);
  const done = async () => {
    setAsk(null);
    await reload();
    await onChange();
  };
  useEffect(() => {
    if (c && c.state !== 'sent' && c.state !== 'sending') setView('email');
  }, [c?.state]);
  // While it goes out, the numbers move: look again every few seconds.
  useEffect(() => {
    if (c?.state !== 'sending') return;
    const timer = setInterval(() => void reload(), 10_000);
    return () => clearInterval(timer);
  }, [c?.state, reload]);
  if (!c) {
    return (
      <Dialog title={t('mkt.campaign')} onClose={onClose} size="xl">
        <Skeleton h={360} />
      </Dialog>
    );
  }
  const manage = can('marketing.manage');
  const send = can('marketing.send');
  const out = c.state === 'sent' || c.state === 'sending' || c.state === 'cancelled';
  return (
    <>
      <Dialog
        title={`${campName(c.number)} · ${c.name}`}
        onClose={onClose}
        size="xl"
        footer={
          <>
            {manage ? (
              <Button
                variant="ghost"
                icon="copy"
                busy={busy}
                onClick={() =>
                  void run(async () => {
                    const copy = await action<{ id: string }>('marketing.duplicate', { id: c.id });
                    await onChange();
                    onOpen(copy.id);
                  }, t('mkt.copied'))
                }
              >
                {t('mkt.duplicate')}
              </Button>
            ) : null}
            {c.state === 'draft' && manage ? (
              <>
                <Button variant="ghost" icon="trash" onClick={() => setAsk('delete')}>
                  {t('mkt.delete')}
                </Button>
                <Button variant="ghost" icon="send" onClick={() => setAsk('test')}>
                  {t('mkt.send_test')}
                </Button>
                <Button icon="edit" onClick={() => onEdit(c.id)}>
                  {t('mkt.edit')}
                </Button>
                {send ? (
                  <Button variant="primary" icon="send" onClick={() => setAsk('send')}>
                    {t('mkt.send')}
                  </Button>
                ) : null}
              </>
            ) : null}
            {(c.state === 'scheduled' || c.state === 'sending') && send ? (
              <Button
                variant="danger"
                icon="x"
                busy={busy}
                onClick={() =>
                  void run(
                    async () => {
                      await action('marketing.cancel', { id: c.id });
                      await done();
                    },
                    c.state === 'scheduled' ? t('mkt.back_to_draft') : t('mkt.stopped'),
                  )
                }
              >
                {c.state === 'scheduled' ? t('mkt.unschedule') : t('mkt.stop_sending')}
              </Button>
            ) : null}
          </>
        }
      >
        <div className="form-stack mkt-dlg">
          <div className="item-summary">
            <div>
              <span className="muted">{t('mkt.audience')}</span>
              <strong>{c.audience_name ?? '—'}</strong>
            </div>
            <div>
              <span className="muted">{out ? t('mkt.c_sent') : t('mkt.will_reach')}</span>
              <strong className="tabular">{out ? `${c.sent} / ${c.recipients}` : (c.audience_count?.reachable ?? '—')}</strong>
            </div>
            <div>
              <span className="muted">{c.state === 'scheduled' ? t('mkt.goes_out') : t('mkt.c_when')}</span>
              <strong>{c.started_at ? dateTime(c.started_at) : c.scheduled_at ? dateTime(c.scheduled_at) : '—'}</strong>
            </div>
            <CampaignBadge c={c} />
          </div>
          {c.last_error && c.state === 'sending' ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} />
              <span>{t('mkt.waiting_server', { error: c.last_error })}</span>
            </p>
          ) : c.state === 'sending' ? (
            <p className="callout callout-info">
              <Icon name="send" size={16} />
              <span>{t('mkt.sending_note', { n: c.queued })}</span>
            </p>
          ) : c.state === 'draft' && c.audience_count ? (
            <p className="callout callout-info">
              <Icon name="users" size={16} />
              <span>
                {t('mkt.reach_note', {
                  n: c.audience_count.reachable,
                  total: c.audience_count.total,
                })}
                {c.tested_at ? ` ${t('mkt.tested_at', { at: dateTime(c.tested_at) })}` : ''}
              </span>
            </p>
          ) : null}
          {out ? (
            <div className="segmented subtle" role="tablist" aria-label={t('mkt.sections')}>
              {(['results', 'email'] as const).map((v) => (
                <button key={v} role="tab" aria-selected={view === v} className={view === v ? 'active' : ''} onClick={() => setView(v)}>
                  {t(`mkt.view_${v}` as MessageKey)}
                </button>
              ))}
            </div>
          ) : null}
          {view === 'results' && out ? <Results c={c} /> : <EmailView c={c} />}
        </div>
      </Dialog>
      {ask === 'send' ? <SendDialog c={c} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'test' ? <TestDialog c={c} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'delete' ? (
        <Dialog
          title={t('mkt.delete_title', { n: campName(c.number) })}
          onClose={() => setAsk(null)}
          size="sm"
          footer={
            <Button
              variant="danger"
              icon="trash"
              busy={busy}
              onClick={() =>
                void run(async () => {
                  await action('marketing.delete', { id: c.id });
                  setAsk(null);
                  onClose();
                  await onChange();
                }, t('mkt.deleted'))
              }
            >
              {t('mkt.delete')}
            </Button>
          }
        >
          <p>{t('mkt.delete_note', { name: c.name })}</p>
        </Dialog>
      ) : null}
    </>
  );
}

/** How the two subject lines did, which one won, and the choice to end the test early. */
function AbPanel({ c }: { c: Campaign }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const [picked, setPicked] = useState<'a' | 'b' | null>(null);
  const ab = c.ab!;
  const winner = c.ab_winner ?? picked;
  const rate = (s: AbSide) => (c.ab_metric === 'clicks' ? s.click_rate : s.open_rate);
  const leader = rate(ab.b) > rate(ab.a) ? 'b' : rate(ab.a) > rate(ab.b) ? 'a' : null;
  const pick = (v: 'a' | 'b') =>
    void run(async () => {
      await action('marketing.pick_winner', { id: c.id, variant: v });
      setPicked(v);
    }, t('ab.picked', { v: v.toUpperCase() }));
  const side = (v: 'a' | 'b') => {
    const s = ab[v];
    const subject = v === 'a' ? c.subject : c.ab_subject;
    return (
      <div key={v} className={`ab-side${winner === v ? ' won' : ''}`}>
        <div className="ab-side-head">
          <span className="ab-letter">{v.toUpperCase()}</span>
          {winner === v ? <StatusBadge tone="ok" icon="star" label={t('ab.winner')} /> : !winner && leader === v ? <StatusBadge tone="info" icon="trendUp" label={t('ab.leading')} /> : null}
        </div>
        <p className="ab-subject">{subject}</p>
        <dl className="ab-figures">
          <div>
            <dt>{t('mkt.opened')}</dt>
            <dd className="tabular">{s.open_rate}%</dd>
          </div>
          <div>
            <dt>{t('mkt.clicked')}</dt>
            <dd className="tabular">{s.click_rate}%</dd>
          </div>
          <div>
            <dt>{t('mkt.c_sent')}</dt>
            <dd className="tabular">{s.sent}</dd>
          </div>
        </dl>
        {!winner && ab.held > 0 && c.state === 'sending' && can('marketing.send') ? (
          <Button size="sm" icon="send" busy={busy} onClick={() => pick(v)}>
            {t('ab.send_to_rest', { v: v.toUpperCase() })}
          </Button>
        ) : null}
      </div>
    );
  };
  return (
    <section className="ab-panel" aria-label={t('ab.results')}>
      <div className="ab-panel-head">
        <h3 className="rep-sub">{t('ab.results')}</h3>
        <p className="muted small">
          {winner
            ? t(c.ab_decided_at && !picked ? 'ab.decided' : 'ab.decided_now', { v: winner.toUpperCase(), when: c.ab_decided_at ? dateTime(c.ab_decided_at) : '' })
            : ab.held > 0 && ab.decide_at
              ? t('ab.waiting', { n: ab.held, when: dateTime(ab.decide_at), metric: t(`ab.m_${c.ab_metric}` as MessageKey).toLowerCase() })
              : t('ab.no_rest')}
        </p>
      </div>
      <div className="ab-sides">{(['a', 'b'] as const).map(side)}</div>
    </section>
  );
}

function Results({ c }: { c: Campaign }) {
  const [filter, setFilter] = useState<(typeof R_FILTERS)[number]>('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const { data } = useAction<{
    total: number;
    rows: {
      id: string;
      email: string;
      name: string | null;
      state: string;
      error: string | null;
      sent_at: string | null;
      opened_at: string | null;
      opens: number;
      clicked_at: string | null;
      clicks: number;
      unsubscribed_at: string | null;
    }[];
  }>('marketing.recipients', {
    campaign: c.id,
    filter,
    ...(q ? { search: q } : {}),
  });
  const hours = c.opens_by_hour;
  return (
    <div className="form-stack mkt-results">
      {c.ab ? <AbPanel c={c} /> : null}
      <section className="rep-kpis mkt-result-kpis">
        <Kpi label={t('mkt.c_sent')} value={String(c.sent)} hint={c.failed ? tn('mkt.n_failed', c.failed) : t('mkt.none_failed')} warn={c.failed > 0} />
        <Kpi label={t('mkt.opened')} value={pct(c.opened, c.sent)} hint={tn('mkt.n_people', c.opened)} />
        <Kpi label={t('mkt.clicked')} value={pct(c.clicked, c.sent)} hint={tn('mkt.n_people', c.clicked)} />
        <Kpi label={t('mkt.unsubscribed')} value={String(c.unsubscribed)} hint={pct(c.unsubscribed, c.sent)} warn={c.sent > 0 && c.unsubscribed / c.sent > 0.02} />
      </section>
      <div className="shop-detail">
        <section>
          <h3 className="rep-sub">{t('mkt.opens_when')}</h3>
          {hours.length ? (
            <Bars
              small
              items={hours.map((h, i) => ({
                key: h.hour,
                label: i % 6 === 0 ? `${h.hour.slice(8, 10)}/${h.hour.slice(5, 7)} ${h.hour.slice(11)}h` : '',
                value: h.n,
                tip: `${h.hour.replace('T', ' ')}:00 · ${h.n}`,
              }))}
            />
          ) : (
            <p className="muted">{t('mkt.no_opens')}</p>
          )}
          <p className="muted small">{t('mkt.opens_note')}</p>
        </section>
        <section>
          <h3 className="rep-sub">{t('mkt.links')}</h3>
          {c.link_stats.length ? (
            <ul className="shop-ways mkt-links">
              {c.link_stats.map((l, i) => (
                <li key={i}>
                  <span className="vat-party">
                    <span className="mkt-url">{l.url.replace(/^https?:\/\//, '')}</span>
                    <span className="muted small">{tn('mkt.n_people', l.people)}</span>
                  </span>
                  <strong className="tabular">{l.clicks}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t('mkt.no_links')}</p>
          )}
        </section>
      </div>
      <section>
        <div className="toolbar">
          <div className="segmented subtle mkt-filters" role="radiogroup" aria-label={t('mkt.show')}>
            {R_FILTERS.map((f) => (
              <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
                {t(`mkt.rf_${f}` as MessageKey)}
              </button>
            ))}
          </div>
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" value={search} placeholder={t('mkt.search_people')} aria-label={t('mkt.search_people')} onChange={(e) => setSearch(e.target.value)} />
          </label>
        </div>
        {!data ? (
          <Skeleton h={200} />
        ) : (
          <>
            <Table
              head={[t('mkt.c_person'), t('mkt.opened'), t('mkt.clicked'), t('mkt.c_state')]}
              phoneHide={[1, 2]}
              left={[0]}
              empty={t('mkt.nobody_here')}
              rows={data.rows.map((r) => ({
                key: r.id,
                cells: [
                  <span className="vat-party" key="p">
                    <span>{r.name ?? r.email}</span>
                    <span className="muted">{r.email}</span>
                  </span>,
                  <span key="o" className="tabular">
                    {r.opened_at ? dateTime(r.opened_at) : '—'}
                  </span>,
                  <span key="c" className="tabular">
                    {r.clicks ? tn('mkt.n_clicks', r.clicks) : '—'}
                  </span>,
                  r.unsubscribed_at ? (
                    <StatusBadge key="s" tone="warn" icon="x" label={t('mkt.unsubscribed')} />
                  ) : r.state === 'failed' || r.state === 'skipped' ? (
                    <span key="s" title={r.error ?? ''}>
                      <StatusBadge tone="danger" icon="alert" label={t(`mkt.r_${r.state}` as MessageKey)} />
                    </span>
                  ) : r.state === 'queued' ? (
                    <StatusBadge key="s" tone="neutral" icon="clock" label={t('mkt.r_queued')} />
                  ) : r.state === 'held' ? (
                    <StatusBadge key="s" tone="neutral" icon="clock" label={t('ab.r_held')} />
                  ) : r.clicked_at ? (
                    <StatusBadge key="s" tone="ok" icon="checkCircle" label={t('mkt.clicked')} />
                  ) : r.opened_at ? (
                    <StatusBadge key="s" tone="info" icon="check" label={t('mkt.opened')} />
                  ) : (
                    <StatusBadge key="s" tone="neutral" icon="send" label={t('mkt.r_sent')} />
                  ),
                ],
              }))}
            />
            {data.total > data.rows.length ? <p className="muted small">{t('mkt.first_100', { n: data.total })}</p> : null}
          </>
        )}
      </section>
    </div>
  );
}

/** The e-mail as it looks, from the server, in a frame that runs nothing. */
function EmailView({ c }: { c: Pick<Campaign, 'subject' | 'preheader' | 'blocks'> }) {
  const [html, setHtml] = useState<{ html: string; from: string } | null>(null);
  const key = JSON.stringify([c.subject, c.preheader, c.blocks]);
  useEffect(() => {
    let live = true;
    const id = setTimeout(() => {
      void action<{ html: string; from: string }>('marketing.preview', {
        subject: c.subject,
        preheader: c.preheader,
        blocks: c.blocks,
      }).then(
        (r) => live && setHtml(r),
        () => live && setHtml(null),
      );
    }, 350);
    return () => {
      live = false;
      clearTimeout(id);
    };
  }, [key]);
  return (
    <div className="mkt-mail">
      <div className="mkt-mail-head">
        <span className="mkt-avatar" aria-hidden="true">
          {(html?.from ?? '?').slice(0, 1)}
        </span>
        <span className="vat-party">
          <strong>{html?.from ?? '…'}</strong>
          <span className="muted small">{c.subject || t('mkt.no_subject')}</span>
        </span>
      </div>
      {html ? <iframe className="mkt-frame" title={t('mkt.preview')} sandbox="" srcDoc={html.html} /> : <Skeleton h={420} />}
    </div>
  );
}

function SendDialog({ c, onClose, onDone }: { c: Campaign; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [at, setAt] = useState(() => {
    const d = new Date(Date.now() + 86_400_000);
    d.setHours(10, 0, 0, 0);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  });
  const reach = c.audience_count?.reachable ?? 0;
  const go = () =>
    void run(
      async () => {
        await action('marketing.schedule', {
          id: c.id,
          at: when === 'later' ? new Date(at).toISOString() : null,
        });
        await onDone();
      },
      when === 'later' ? t('mkt.scheduled_msg') : t('mkt.sending_msg'),
    );
  return (
    <Dialog
      title={t('mkt.send_title', { name: c.name })}
      onClose={onClose}
      size="sm"
      footer={
        <Button variant="primary" icon={when === 'later' ? 'clock' : 'send'} busy={busy} onClick={go} disabled={!reach}>
          {when === 'later' ? t('mkt.schedule') : tn('mkt.send_to', reach)}
        </Button>
      }
    >
      <div className="form-stack">
        <p>{t('mkt.send_note', { n: reach, audience: c.audience_name ?? '' })}</p>
        {!c.tested_at ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t('mkt.not_tested')}</span>
          </p>
        ) : null}
        <div className="option-list" role="radiogroup" aria-label={t('mkt.when')}>
          {(['now', 'later'] as const).map((w) => (
            <label key={w} className={`option-card${when === w ? ' selected' : ''}`}>
              <input type="radio" name="when" checked={when === w} onChange={() => setWhen(w)} />
              <span>
                <strong>{t(`mkt.when_${w}` as MessageKey)}</strong>
                <span className="muted small">{t(`mkt.when_${w}_hint` as MessageKey)}</span>
              </span>
            </label>
          ))}
        </div>
        {when === 'later' ? (
          <Field label={t('mkt.send_at')}>
            <input className="input" type="datetime-local" value={at} onChange={(e) => e.target.value && setAt(e.target.value)} />
          </Field>
        ) : null}
      </div>
    </Dialog>
  );
}

function TestDialog({ c, onClose, onDone }: { c: Campaign; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [to, setTo] = useState('');
  const list = to
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const bad = list.some((x) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
  return (
    <Dialog
      title={t('mkt.test_title')}
      onClose={onClose}
      size="sm"
      footer={
        <Button
          variant="primary"
          icon="send"
          busy={busy}
          disabled={!list.length || bad || list.length > 5}
          onClick={() =>
            void run(async () => {
              await action('marketing.test', { id: c.id, to: list });
              await onDone();
            }, t('mkt.test_sent'))
          }
        >
          {t('mkt.send_test')}
        </Button>
      }
    >
      <Field label={t('mkt.test_to')} hint={t('mkt.test_hint')} error={bad ? t('mkt.e_email') : list.length > 5 ? t('mkt.e_five') : null}>
        <input className="input" type="email" multiple value={to} placeholder="you@example.com" onChange={(e) => setTo(e.target.value)} />
      </Field>
    </Dialog>
  );
}

// ---- the editor -----------------------------------------------------------------------------------------

const BLOCKS: { type: BlockType; icon: IconName }[] = [
  { type: 'heading', icon: 'note' },
  { type: 'text', icon: 'list' },
  { type: 'button', icon: 'send' },
  { type: 'image', icon: 'image' },
  { type: 'products', icon: 'layers' },
  { type: 'offer', icon: 'gift' },
  { type: 'divider', icon: 'minus' },
];
const newBlock = (type: BlockType): Block =>
  type === 'heading'
    ? { type, text: '' }
    : type === 'text'
      ? { type, text: '' }
      : type === 'button'
        ? { type, label: '', url: 'https://' }
        : type === 'image'
          ? { type, url: 'https://', alt: '', link: null }
          : type === 'products'
            ? { type, ids: [], url: null }
            : type === 'offer'
              ? { type, title: '', code: null, note: null }
              : { type };
const isUrl = (u: string | null | undefined) => !!u && /^https?:\/\/[^\s<>"']+\.[^\s<>"']+$/i.test(u.trim());
function blockError(b: Block): string | null {
  if (b.type === 'heading' && !b.text.trim()) return t('mkt.e_text');
  if (b.type === 'text' && !b.text.trim()) return t('mkt.e_text');
  if (b.type === 'button' && (!b.label.trim() || !isUrl(b.url))) return !b.label.trim() ? t('mkt.e_label') : t('mkt.e_url');
  if (b.type === 'image' && (!isUrl(b.url) || (b.link && !isUrl(b.link)))) return t('mkt.e_url');
  if (b.type === 'products' && (!b.ids.length || (b.url && !isUrl(b.url)))) return !b.ids.length ? t('mkt.e_products') : t('mkt.e_url');
  if (b.type === 'offer' && !b.title.trim()) return t('mkt.e_text');
  return null;
}
const clean = (b: Block): Block =>
  b.type === 'image'
    ? {
        ...b,
        link: b.link?.trim() && b.link.trim() !== 'https://' ? b.link.trim() : null,
      }
    : b.type === 'products'
      ? {
          ...b,
          url: b.url?.trim() && b.url.trim() !== 'https://' ? b.url.trim() : null,
        }
      : b.type === 'offer'
        ? { ...b, code: b.code?.trim() || null, note: b.note?.trim() || null }
        : b;

/** Writes a campaign, or one e-mail of a journey (`step`: no audience of its own, it is the journey's). */
export function Editor({ id, onClose, onSaved, step }: { id: string | null; onClose: () => void; onSaved: (id: string) => Promise<void>; step?: { label: string } }) {
  const { busy, run } = useSave();
  const { data: setup } = useAction<Setup>('marketing.setup');
  const [ready, setReady] = useState(!id);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [audience, setAudience] = useState('');
  // A/B test of the subject line.
  const [abOn, setAbOn] = useState(false);
  const [abSubject, setAbSubject] = useState('');
  const [abPreheader, setAbPreheader] = useState('');
  const [abPercent, setAbPercent] = useState(20);
  const [abHours, setAbHours] = useState(4);
  const [abMetric, setAbMetric] = useState<AbMetric>('opens');
  const [blocks, setBlocks] = useState<Block[]>([
    { type: 'heading', text: '' },
    { type: 'text', text: '' },
  ]);
  const [tried, setTried] = useState(false);
  const [phoneView, setPhoneView] = useState<'edit' | 'preview'>('edit');
  useEffect(() => {
    if (!id) return;
    void action<Campaign>('marketing.campaign', { id }).then((c) => {
      setName(c.name);
      setSubject(c.subject);
      setPreheader(c.preheader ?? '');
      setAudience(c.audience ?? '');
      if (c.ab_percent > 0 && c.ab_subject) {
        setAbOn(true);
        setAbSubject(c.ab_subject);
        setAbPreheader(c.ab_preheader ?? '');
        setAbPercent(c.ab_percent);
        setAbHours(c.ab_hours);
        setAbMetric(c.ab_metric);
      }
      setBlocks(c.blocks.length ? c.blocks : [newBlock('heading'), newBlock('text')]);
      setReady(true);
    });
  }, [id]);
  useEffect(() => {
    const best = setup?.audiences.reduce<Setup['audiences'][number] | null>((b, x) => (!b || x.reachable > b.reachable ? x : b), null);
    if (!audience && best && !step) setAudience(best.id);
  }, [setup]);
  const set = (i: number, b: Block) => setBlocks(blocks.map((x, j) => (j === i ? b : x)));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setBlocks(next);
  };
  const errors = {
    name: !name.trim() ? t('mkt.e_name') : null,
    subject: !subject.trim() ? t('mkt.e_subject') : null,
    ab: !abOn || step ? null : !abSubject.trim() ? t('ab.e_subject') : abSubject.trim().toLowerCase() === subject.trim().toLowerCase() ? t('ab.e_same') : null,
    blocks: blocks.some(blockError) ? t('mkt.e_blocks') : !blocks.length ? t('mkt.e_empty') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      const c = await action<{ id: string }>('marketing.save', {
        id,
        name: name.trim(),
        subject: subject.trim(),
        preheader: preheader.trim() || null,
        blocks: blocks.map(clean),
        audience: audience || null,
        ...(step ? {} : { ab: abOn ? { subject: abSubject.trim(), preheader: abPreheader.trim() || null, percent: abPercent, hours: abHours, metric: abMetric } : null }),
      });
      await onSaved(c.id);
    }, t('mkt.saved'));
  };
  const previewBlocks = useMemo(() => blocks.filter((b) => !blockError(b)).map(clean), [blocks]);
  const aud = setup?.audiences.find((a) => a.id === audience);
  return (
    <Dialog
      title={step ? step.label : id ? t('mkt.edit_title') : t('mkt.new')}
      onClose={onClose}
      size="xl"
      footer={
        <>
          {step ? (
            <span className="muted small">{t('jr.editor_hint')}</span>
          ) : (
            <span className="inv-foot-total">
              <span className="muted">{t('mkt.will_reach')}</span>
              <strong>{aud ? tn('mkt.n_people', aud.reachable) : '—'}</strong>
            </span>
          )}
          <span className="foot-actions">
            <Button variant="primary" icon="check" busy={busy} onClick={save}>
              {step ? t('common.save') : t('mkt.save_draft')}
            </Button>
          </span>
        </>
      }
    >
      {!setup || !ready ? (
        <Skeleton h={420} />
      ) : (
        <div className={`mkt-editor show-${phoneView}`}>
          <div className="segmented subtle mkt-phone-switch" role="tablist" aria-label={t('mkt.sections')}>
            {(['edit', 'preview'] as const).map((v) => (
              <button key={v} role="tab" aria-selected={phoneView === v} className={phoneView === v ? 'active' : ''} onClick={() => setPhoneView(v)}>
                {t(`mkt.pv_${v}` as MessageKey)}
              </button>
            ))}
          </div>
          <div className="mkt-edit form-stack sub-form">
            <div className="form-grid">
              <Field label={t('mkt.name')} required error={tried ? errors.name : null} hint={t('mkt.name_hint')}>
                <input className="input" value={name} maxLength={120} placeholder={t('mkt.name_example')} onChange={(e) => setName(e.target.value)} />
              </Field>
              {step ? null : (
                <Field label={t('mkt.audience')} hint={aud ? tn('mkt.n_agreed', aud.reachable) : t('mkt.audience_hint')}>
                  <select className="input" value={audience} onChange={(e) => setAudience(e.target.value)}>
                    <option value="">{t('mkt.choose_later')}</option>
                    {setup.audiences.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.reachable})
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </div>
            <Field label={t('mkt.subject')} required error={tried ? errors.subject : null} counter={`${subject.length}/150`} hint={t('mkt.subject_hint')}>
              <input className="input" value={subject} maxLength={150} onChange={(e) => setSubject(e.target.value)} />
            </Field>
            <Field label={t('mkt.preheader')} counter={`${preheader.length}/150`} hint={t('mkt.preheader_hint')}>
              <input className="input" value={preheader} maxLength={150} onChange={(e) => setPreheader(e.target.value)} />
            </Field>
            {step ? null : (
              <section className={`ab-box${abOn ? ' on' : ''}`} aria-label={t('ab.title')}>
                <div className="ab-head">
                  <span className="ab-badge" aria-hidden="true">
                    A/B
                  </span>
                  <div>
                    <strong>{t('ab.title')}</strong>
                    <p className="muted small">{t('ab.intro')}</p>
                  </div>
                  <Switch checked={abOn} label={t('ab.switch')} onChange={setAbOn} />
                </div>
                {abOn ? (
                  <div className="form-stack">
                    <Field label={t('ab.subject_b')} required error={tried ? errors.ab : null} counter={`${abSubject.length}/150`} hint={t('ab.subject_b_hint')}>
                      <input className="input" value={abSubject} maxLength={150} onChange={(e) => setAbSubject(e.target.value)} />
                    </Field>
                    <Field label={t('ab.preheader_b')} counter={`${abPreheader.length}/150`}>
                      <input className="input" value={abPreheader} maxLength={150} placeholder={preheader} onChange={(e) => setAbPreheader(e.target.value)} />
                    </Field>
                    <div className="form-grid">
                      <Field label={t('ab.share')} hint={aud ? t('ab.share_hint', { n: Math.max(1, Math.floor((aud.reachable * abPercent) / 200)) }) : undefined}>
                        <select className="input" value={abPercent} onChange={(e) => setAbPercent(Number(e.target.value))}>
                          {AB_PERCENTS.map((p) => (
                            <option key={p} value={p}>
                              {t('ab.share_n', { n: p })}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label={t('ab.wait')} hint={t('ab.wait_hint')}>
                        <select className="input" value={abHours} onChange={(e) => setAbHours(Number(e.target.value))}>
                          {AB_HOURS.map((h) => (
                            <option key={h} value={h}>
                              {tn('ab.hours', h)}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <div className="field">
                      <span className="field-label">{t('ab.metric')}</span>
                      <div className="segmented subtle" role="radiogroup" aria-label={t('ab.metric')}>
                        {(['opens', 'clicks'] as const).map((m) => (
                          <button key={m} type="button" role="radio" aria-checked={abMetric === m} className={abMetric === m ? 'active' : ''} onClick={() => setAbMetric(m)}>
                            {t(`ab.m_${m}` as MessageKey)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </section>
            )}
            <fieldset className="option-group">
              <legend>{t('mkt.content')}</legend>
              <p className="muted small">{t('mkt.merge_hint')}</p>
              <ol className="mkt-blocks">
                {blocks.map((b, i) => (
                  <li key={i} className={tried && blockError(b) ? 'invalid' : ''}>
                    <div className="mkt-block-head">
                      <span className="mkt-block-kind">
                        <Icon name={BLOCKS.find((x) => x.type === b.type)!.icon} size={14} />
                        {t(`mkt.b_${b.type}` as MessageKey)}
                      </span>
                      <span className="row-actions">
                        <IconButton icon="up" label={t('mkt.move_up')} disabled={i === 0} onClick={() => move(i, -1)} />
                        <IconButton icon="down" label={t('mkt.move_down')} disabled={i === blocks.length - 1} onClick={() => move(i, 1)} />
                        <IconButton icon="trash" label={t('mkt.remove_block')} onClick={() => setBlocks(blocks.filter((_, j) => j !== i))} />
                      </span>
                    </div>
                    <BlockFields b={b} set={(x) => set(i, x)} products={setup.products} />
                    {tried && blockError(b) ? (
                      <p className="field-msg" role="alert">
                        <Icon name="alert" size={14} /> {blockError(b)}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ol>
              <div className="mkt-add" role="group" aria-label={t('mkt.add_block')}>
                {BLOCKS.map((x) => (
                  <Button key={x.type} size="sm" icon={x.icon} onClick={() => setBlocks([...blocks, newBlock(x.type)])}>
                    {t(`mkt.b_${x.type}` as MessageKey)}
                  </Button>
                ))}
              </div>
              {tried && errors.blocks ? (
                <p className="field-msg" role="alert">
                  <Icon name="alert" size={14} /> {errors.blocks}
                </p>
              ) : null}
            </fieldset>
          </div>
          <div className="mkt-preview">
            <EmailView
              c={{
                subject,
                preheader: preheader || null,
                blocks: previewBlocks,
              }}
            />
          </div>
        </div>
      )}
    </Dialog>
  );
}

function BlockFields({ b, set, products }: { b: Block; set: (b: Block) => void; products: Setup['products'] }) {
  if (b.type === 'heading')
    return <input className="input" aria-label={t('mkt.b_heading')} value={b.text} maxLength={160} placeholder={t('mkt.heading_example')} onChange={(e) => set({ ...b, text: e.target.value })} />;
  if (b.type === 'text')
    return (
      <textarea
        className="input textarea"
        aria-label={t('mkt.b_text')}
        rows={4}
        value={b.text}
        maxLength={4000}
        placeholder={t('mkt.text_example')}
        onChange={(e) => set({ ...b, text: e.target.value })}
      />
    );
  if (b.type === 'button')
    return (
      <div className="form-grid">
        <input className="input" aria-label={t('mkt.button_label')} placeholder={t('mkt.button_label')} value={b.label} maxLength={60} onChange={(e) => set({ ...b, label: e.target.value })} />
        <input className="input" type="url" aria-label={t('mkt.link')} placeholder="https://" value={b.url} onChange={(e) => set({ ...b, url: e.target.value })} />
      </div>
    );
  if (b.type === 'image')
    return (
      <div className="form-grid">
        <input className="input" type="url" aria-label={t('mkt.image_url')} placeholder={t('mkt.image_url')} value={b.url} onChange={(e) => set({ ...b, url: e.target.value })} />
        <input className="input" aria-label={t('mkt.image_alt')} placeholder={t('mkt.image_alt')} value={b.alt} maxLength={160} onChange={(e) => set({ ...b, alt: e.target.value })} />
        <input className="input" type="url" aria-label={t('mkt.image_link')} placeholder={t('mkt.image_link')} value={b.link ?? ''} onChange={(e) => set({ ...b, link: e.target.value || null })} />
      </div>
    );
  if (b.type === 'products') {
    const left = products.filter((p) => !b.ids.includes(p.id));
    return (
      <div className="form-stack">
        <ul className="mkt-chips">
          {b.ids.map((id) => {
            const p = products.find((x) => x.id === id);
            return (
              <li key={id}>
                <span>
                  {p?.name ?? '—'} <span className="muted">{p?.price}</span>
                </span>
                <IconButton icon="x" label={t('mkt.remove')} onClick={() => set({ ...b, ids: b.ids.filter((x) => x !== id) })} />
              </li>
            );
          })}
        </ul>
        <div className="form-grid">
          <select className="input" aria-label={t('mkt.add_product')} value="" disabled={b.ids.length >= 6} onChange={(e) => e.target.value && set({ ...b, ids: [...b.ids, e.target.value] })}>
            <option value="">{b.ids.length >= 6 ? t('mkt.six_max') : t('mkt.add_product')}</option>
            {left.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} · {p.price}
              </option>
            ))}
          </select>
          <input
            className="input"
            type="url"
            aria-label={t('mkt.products_link')}
            placeholder={t('mkt.products_link')}
            value={b.url ?? ''}
            onChange={(e) => set({ ...b, url: e.target.value || null })}
          />
        </div>
      </div>
    );
  }
  if (b.type === 'offer')
    return (
      <div className="form-grid">
        <input className="input" aria-label={t('mkt.offer_title')} placeholder={t('mkt.offer_title')} value={b.title} maxLength={120} onChange={(e) => set({ ...b, title: e.target.value })} />
        <input
          className="input"
          aria-label={t('mkt.offer_code')}
          placeholder={t('mkt.offer_code')}
          value={b.code ?? ''}
          maxLength={40}
          onChange={(e) => set({ ...b, code: e.target.value.toUpperCase() || null })}
        />
        <input
          className="input"
          aria-label={t('mkt.offer_note')}
          placeholder={t('mkt.offer_note')}
          value={b.note ?? ''}
          maxLength={300}
          onChange={(e) => set({ ...b, note: e.target.value || null })}
        />
      </div>
    );
  return <p className="muted small">{t('mkt.divider_hint')}</p>;
}

// ---- audiences -----------------------------------------------------------------------------------------

function AudiencesTab({ onChange }: { onChange: () => Promise<void> }) {
  const { can } = useSession();
  const { data, reload } = useAction<AudienceRow[]>('marketing.audiences');
  const [edit, setEdit] = useState<AudienceRow | 'new' | null>(null);
  const manage = can('marketing.manage');
  return (
    <section className="panel">
      <div className="prot-history-head">
        <div>
          <h2 className="rep-sub">{t('mkt.audiences')}</h2>
          <p className="muted small">{t('mkt.audiences_hint')}</p>
        </div>
        {manage ? (
          <Button size="sm" icon="plus" onClick={() => setEdit('new')}>
            {t('mkt.new_audience')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : data.length ? (
        <ul className="sub-plans">
          {data.map((a) => (
            <li key={a.id} className={a.active ? '' : 'inactive'}>
              <div className="sub-plan-head">
                <strong>{a.name}</strong>
                <StatusBadge tone="neutral" icon={a.kind === 'list' ? 'list' : 'sliders'} label={t(`mkt.kind_${a.kind}` as MessageKey)} />
              </div>
              <span className="sub-plan-price">
                <strong className="tabular">{a.reachable}</strong>
                <span className="muted small">{t('mkt.can_write_to')}</span>
              </span>
              <span className="muted small">{a.kind === 'rules' ? describeRules(a.rules) : (a.description ?? tn('mkt.n_members', a.total))}</span>
              <span className="muted small">
                {[a.total - a.reachable ? t('mkt.not_reachable', { n: a.total - a.reachable }) : null, a.campaigns ? tn('mkt.n_campaigns', a.campaigns) : null].filter(Boolean).join(' · ')}
              </span>
              {manage ? (
                <div className="row-actions">
                  <Button size="sm" variant="ghost" icon="edit" onClick={() => setEdit(a)}>
                    {t('mkt.edit')}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="users" title={t('mkt.no_audiences')} body={t('mkt.no_audiences_hint')} />
      )}
      {edit ? (
        <AudienceDialog
          a={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={async () => {
            setEdit(null);
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </section>
  );
}

function describeRules(r: Rules) {
  const parts = [
    r.who !== 'all' ? t(`mkt.who_${r.who}` as MessageKey) : null,
    r.vip ? t('mkt.vip_only') : null,
    r.towns.length ? r.towns.join(', ') : null,
    r.bought_within ? t('mkt.d_bought', { n: r.bought_within }) : null,
    r.away_for ? t('mkt.d_away', { n: r.away_for }) : null,
    r.spent_at_least ? t('mkt.d_spent', { amount: money(r.spent_at_least) }) : null,
    r.birthday_month === 0 ? t('mkt.d_birthday_now') : r.birthday_month ? t('mkt.d_birthday', { month: monthName(r.birthday_month) }) : null,
    r.new_within ? t('mkt.d_new', { n: r.new_within }) : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : t('mkt.everyone');
}
const monthName = (m: number) => new Intl.DateTimeFormat(undefined, { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, m - 1, 15)));

function NumberField({ label, hint, value, set, unit }: { label: string; hint?: string; value: number | null; set: (n: number | null) => void; unit: string }) {
  return (
    <Field label={label} hint={hint}>
      <span className="input-unit">
        <input
          className="input"
          inputMode="numeric"
          value={value ?? ''}
          placeholder="—"
          onChange={(e) => set(e.target.value.replace(/\D/g, '') ? Math.min(3650, Number(e.target.value.replace(/\D/g, ''))) : null)}
        />
        <span>{unit}</span>
      </span>
    </Field>
  );
}

function AudienceDialog({ a, onClose, onSaved }: { a: AudienceRow | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: setup } = useAction<Setup>('marketing.setup');
  const [name, setName] = useState(a?.name ?? '');
  const [kind, setKind] = useState<'rules' | 'list'>(a?.kind ?? 'rules');
  const [rules, setRules] = useState<Rules>(a?.rules ?? blankRules());
  const [description, setDescription] = useState(a?.description ?? '');
  const [spent, setSpent] = useState(a?.rules.spent_at_least ? String(a.rules.spent_at_least / 100) : '');
  const [preview, setPreview] = useState<(Count & { sample: { id: string; name: string; email: string }[] }) | null>(null);
  const [tried, setTried] = useState(false);
  const [ask, setAsk] = useState(false);
  const r = (patch: Partial<Rules>) => setRules({ ...rules, ...patch });
  useEffect(() => {
    if (kind !== 'rules') return;
    const id = setTimeout(
      () =>
        void action<typeof preview>('marketing.audience_preview', {
          rules,
        }).then(setPreview, () => setPreview(null)),
      300,
    );
    return () => clearTimeout(id);
  }, [JSON.stringify(rules), kind]);
  const save = () => {
    setTried(true);
    if (!name.trim()) return;
    void run(async () => {
      await action('marketing.save_audience', {
        id: a?.id ?? null,
        name: name.trim(),
        kind,
        rules,
        description: description.trim() || null,
      });
      await onSaved();
    }, t('mkt.audience_saved'));
  };
  return (
    <>
      <Dialog
        title={a ? a.name : t('mkt.new_audience')}
        onClose={onClose}
        size="lg"
        footer={
          <>
            {a ? (
              <Button variant="ghost" icon="trash" onClick={() => setAsk(true)}>
                {t('mkt.delete')}
              </Button>
            ) : null}
            <span className="foot-actions">
              <Button variant="primary" icon="check" busy={busy} onClick={save}>
                {t('common.save')}
              </Button>
            </span>
          </>
        }
      >
        {!setup ? (
          <Skeleton h={300} />
        ) : (
          <div className="form-stack sub-form">
            <Field label={t('mkt.name')} required error={tried && !name.trim() ? t('mkt.e_name') : null}>
              <input className="input" value={name} maxLength={80} placeholder={t('mkt.audience_example')} onChange={(e) => setName(e.target.value)} />
            </Field>
            <div className="option-list" role="radiogroup" aria-label={t('mkt.kind')}>
              {(['rules', 'list'] as const).map((k) => (
                <label key={k} className={`option-card${kind === k ? ' selected' : ''}`}>
                  <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} />
                  <span>
                    <strong>{t(`mkt.kind_${k}` as MessageKey)}</strong>
                    <span className="muted small">{t(`mkt.kind_${k}_hint` as MessageKey)}</span>
                  </span>
                </label>
              ))}
            </div>
            {kind === 'rules' ? (
              <>
                <Field label={t('mkt.who')}>
                  <div className="segmented subtle" role="radiogroup" aria-label={t('mkt.who')}>
                    {(['all', 'people', 'companies'] as const).map((w) => (
                      <button key={w} type="button" role="radio" aria-checked={rules.who === w} className={rules.who === w ? 'active' : ''} onClick={() => r({ who: w })}>
                        {t(`mkt.who_${w}` as MessageKey)}
                      </button>
                    ))}
                  </div>
                </Field>
                {setup.sales ? (
                  <div className="form-grid">
                    <NumberField label={t('mkt.bought_within')} hint={t('mkt.bought_within_hint')} value={rules.bought_within} set={(n) => r({ bought_within: n })} unit={t('mkt.days')} />
                    <NumberField label={t('mkt.away_for')} hint={t('mkt.away_for_hint')} value={rules.away_for} set={(n) => r({ away_for: n })} unit={t('mkt.days')} />
                    <Field label={t('mkt.spent')} hint={t('mkt.spent_hint')}>
                      <input
                        className="input"
                        inputMode="decimal"
                        value={spent}
                        placeholder="—"
                        onChange={(e) => {
                          setSpent(e.target.value);
                          const v = parseMoney(e.target.value);
                          r({ spent_at_least: v && v > 0 ? v : null });
                        }}
                      />
                    </Field>
                  </div>
                ) : null}
                <div className="form-grid">
                  <Field label={t('mkt.birthday')}>
                    <select
                      className="input"
                      value={rules.birthday_month ?? ''}
                      onChange={(e) =>
                        r({
                          birthday_month: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    >
                      <option value="">{t('mkt.any')}</option>
                      <option value="0">{t('mkt.d_birthday_now')}</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {monthName(i + 1)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <NumberField label={t('mkt.new_within')} value={rules.new_within} set={(n) => r({ new_within: n })} unit={t('mkt.days')} />
                  <Field label={t('mkt.towns')} hint={t('mkt.towns_hint')}>
                    <select className="input" value="" onChange={(e) => e.target.value && !rules.towns.includes(e.target.value) && r({ towns: [...rules.towns, e.target.value] })}>
                      <option value="">{rules.towns.length ? t('mkt.add_town') : t('mkt.any')}</option>
                      {setup.towns
                        .filter((x) => !rules.towns.includes(x))
                        .map((x) => (
                          <option key={x} value={x}>
                            {x}
                          </option>
                        ))}
                    </select>
                  </Field>
                </div>
                {rules.towns.length ? (
                  <ul className="mkt-chips">
                    {rules.towns.map((x) => (
                      <li key={x}>
                        <span>{x}</span>
                        <IconButton icon="x" label={t('mkt.remove')} onClick={() => r({ towns: rules.towns.filter((y) => y !== x) })} />
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className="setting-row">
                  <div>
                    <strong>{t('mkt.vip_only')}</strong>
                    <p className="muted">{t('mkt.vip_hint')}</p>
                  </div>
                  <Switch checked={rules.vip} onChange={(v) => r({ vip: v })} label={t('mkt.vip_only')} />
                </div>
                <section className="mkt-count" aria-live="polite">
                  {preview ? (
                    <>
                      <strong className="tabular">{tn('mkt.n_people', preview.reachable)}</strong>
                      <span className="muted small">
                        {t('mkt.count_detail', {
                          total: preview.total,
                          no_email: preview.no_email,
                          not_agreed: preview.not_agreed,
                          out: preview.opted_out,
                        })}
                      </span>
                      {preview.sample.length ? (
                        <span className="small">
                          {preview.sample.map((s) => s.name).join(', ')}
                          {preview.reachable > preview.sample.length ? '…' : ''}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <Skeleton h={40} />
                  )}
                </section>
              </>
            ) : a?.kind === 'list' ? (
              <Members audience={a.id} />
            ) : (
              <p className="muted">{t('mkt.list_after_save')}</p>
            )}
            <Field label={t('mkt.description')} counter={`${description.length}/500`}>
              <textarea className="input textarea" rows={2} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
            </Field>
          </div>
        )}
      </Dialog>
      {ask && a ? (
        <Dialog
          title={t('mkt.delete_audience', { name: a.name })}
          onClose={() => setAsk(false)}
          size="sm"
          footer={
            <Button
              variant="danger"
              icon="trash"
              busy={busy}
              onClick={() =>
                void run(async () => {
                  await action('marketing.delete_audience', { id: a.id });
                  setAsk(false);
                  await onSaved();
                }, t('mkt.deleted'))
              }
            >
              {t('mkt.delete')}
            </Button>
          }
        >
          <p>{t('mkt.delete_audience_note')}</p>
        </Dialog>
      ) : null}
    </>
  );
}

function Members({ audience }: { audience: string }) {
  const { run } = useSave();
  const { data, reload } = useAction<{
    members: {
      id: string;
      name: string;
      email: string | null;
      reachable: boolean;
    }[];
  }>('marketing.audience', { id: audience });
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<Contact[]>([]);
  useEffect(() => {
    if (search.trim().length < 2) return setFound([]);
    const id = setTimeout(
      () =>
        void action<{ rows: Contact[] }>('marketing.contacts', {
          filter: 'all',
          search: search.trim(),
        }).then((r) => setFound(r.rows.slice(0, 8))),
      250,
    );
    return () => clearTimeout(id);
  }, [search]);
  const change = (body: { add?: string[]; remove?: string[] }) =>
    void run(async () => {
      await action('marketing.members', { audience, ...body });
      await reload();
    });
  const ids = new Set(data?.members.map((m) => m.id));
  return (
    <section className="form-stack">
      <Field label={t('mkt.add_people')}>
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={search} placeholder={t('mkt.search_customers')} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </Field>
      {found.length ? (
        <ul className="shop-ways">
          {found.map((f) => (
            <li key={f.id}>
              <span className="vat-party">
                <span>{f.name}</span>
                <span className="muted small">{f.email ?? t('mkt.no_email')}</span>
              </span>
              {ids.has(f.id) ? (
                <span className="muted small">{t('mkt.in_list')}</span>
              ) : (
                <Button size="sm" icon="plus" onClick={() => change({ add: [f.id] })}>
                  {t('mkt.add')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {!data ? (
        <Skeleton h={120} />
      ) : data.members.length ? (
        <ul className="shop-ways mkt-members">
          {data.members.map((m) => (
            <li key={m.id}>
              <span className="vat-party">
                <span>{m.name}</span>
                <span className="muted small">{m.reachable ? m.email : t('mkt.cannot_write')}</span>
              </span>
              <IconButton icon="x" label={t('mkt.remove')} onClick={() => change({ remove: [m.id] })} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t('mkt.list_empty')}</p>
      )}
    </section>
  );
}

// ---- contacts ------------------------------------------------------------------------------------------

const K_FILTERS = ['agreed', 'not_agreed', 'opted_out', 'no_email', 'all'] as const;

function ContactsTab({ params, set, onChange }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const filter = (K_FILTERS as readonly string[]).includes(params.get('f') ?? '') ? (params.get('f') as (typeof K_FILTERS)[number]) : 'agreed';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [consent, setConsent] = useState<Contact | null>(null);
  useEffect(() => {
    const id = setTimeout(() => (setQ(search.trim()), setOffset(0)), 300);
    return () => clearTimeout(id);
  }, [search]);
  const { data, reload } = useAction<{ total: number; rows: Contact[] }>('marketing.contacts', { filter, offset, ...(q ? { search: q } : {}) });
  const manage = can('marketing.manage');
  return (
    <section className="panel">
      <div className="toolbar">
        <div className="segmented subtle mkt-filters" role="radiogroup" aria-label={t('mkt.show')}>
          {K_FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => (set({ f: f === 'agreed' ? null : f }), setOffset(0))}>
              {t(`mkt.kf_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={search} placeholder={t('mkt.search_customers')} aria-label={t('mkt.search_customers')} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : (
        <>
          <Table
            head={[t('mkt.c_person'), t('mkt.c_agreement'), t('mkt.c_received'), '']}
            phoneHide={[2, 3]}
            left={[0, 1]}
            empty={t('mkt.nobody_here')}
            rows={data.rows.map((p) => ({
              key: p.id,
              ...(manage ? { onClick: () => setConsent(p) } : {}),
              cells: [
                <span className="vat-party" key="p">
                  <span>{p.name}</span>
                  <span className="muted">{p.email ?? t('mkt.no_email')}</span>
                </span>,
                p.reachable ? (
                  <span key="a" className="vat-party">
                    <StatusBadge tone="ok" icon="checkCircle" label={t('mkt.agreed')} />
                    <span className="muted small">{p.marketing_at ? dateTime(p.marketing_at) : ''}</span>
                  </span>
                ) : p.opted_out_at && (!p.marketing_at || p.opted_out_at >= p.marketing_at) ? (
                  <span key="a" className="vat-party">
                    <StatusBadge tone="warn" icon="x" label={t('mkt.opted_out')} />
                    <span className="muted small">{dateTime(p.opted_out_at)}</span>
                  </span>
                ) : (
                  <StatusBadge key="a" tone="neutral" icon="minus" label={p.email ? t('mkt.not_agreed') : t('mkt.no_email')} />
                ),
                <span key="r" className="tabular">
                  {p.received ? t('mkt.received_opened', { n: p.received, o: p.opened }) : '—'}
                </span>,
                manage ? (
                  <Button key="b" size="sm" variant="ghost" icon={p.reachable ? 'x' : 'check'} onClick={() => setConsent(p)}>
                    {p.reachable ? t('mkt.withdraw') : t('mkt.record_agreement')}
                  </Button>
                ) : null,
              ],
            }))}
          />
          <div className="row-actions mkt-pager">
            <span className="muted small">
              {t('mkt.showing', {
                from: data.total ? offset + 1 : 0,
                to: offset + data.rows.length,
                total: data.total,
              })}
            </span>
            <IconButton icon="back" label={t('mkt.prev')} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 100))} />
            <IconButton icon="chevronRight" label={t('mkt.next')} disabled={offset + data.rows.length >= data.total} onClick={() => setOffset(offset + 100)} />
          </div>
        </>
      )}
      {consent ? (
        <ConsentDialog
          p={consent}
          onClose={() => setConsent(null)}
          onDone={async () => {
            setConsent(null);
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </section>
  );
}

function ConsentDialog({ p, onClose, onDone }: { p: Contact; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const agree = !p.reachable;
  const [email, setEmail] = useState(p.email ?? '');
  const [sure, setSure] = useState(false);
  const [note, setNote] = useState('');
  const bad = agree && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  return (
    <Dialog
      title={agree ? t('mkt.agree_title', { name: p.name }) : t('mkt.withdraw_title', { name: p.name })}
      onClose={onClose}
      size="sm"
      footer={
        <Button
          variant={agree ? 'primary' : 'danger'}
          icon={agree ? 'check' : 'x'}
          busy={busy}
          disabled={(agree && (!sure || bad)) || false}
          onClick={() =>
            void run(
              async () => {
                await action('marketing.set_consent', {
                  customer: p.id,
                  agreed: agree,
                  email: agree ? email.trim() : null,
                  note: note.trim() || null,
                });
                await onDone();
              },
              agree ? t('mkt.agreement_saved') : t('mkt.withdrawn'),
            )
          }
        >
          {agree ? t('mkt.record_agreement') : t('mkt.withdraw')}
        </Button>
      }
    >
      <div className="form-stack">
        {agree ? (
          <>
            {p.opted_out_at ? (
              <p className="callout callout-warn">
                <Icon name="alert" size={16} />
                <span>{t('mkt.was_out', { at: dateTime(p.opted_out_at) })}</span>
              </p>
            ) : null}
            <Field label={t('mkt.email')} error={email && bad ? t('mkt.e_email') : null}>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <label className="check-row">
              <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} />
              <span>{t('mkt.agree_confirm')}</span>
            </label>
          </>
        ) : (
          <>
            <p>{t('mkt.withdraw_note')}</p>
            <Field label={t('mkt.note')} hint={t('mkt.optional')}>
              <input className="input" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </>
        )}
      </div>
    </Dialog>
  );
}

// ---- settings --------------------------------------------------------------------------------------------

interface MailSettings {
  host: string | null;
  port: number;
  security: 'ssl' | 'starttls' | 'none';
  username: string | null;
  from_name: string | null;
  from_email: string | null;
  reply_to: string | null;
  address: string | null;
  site_url: string | null;
  accent: string;
  per_minute: number;
  per_day: number;
  verified_at: string | null;
  last_error: string | null;
  has_password: boolean;
  password_readable: boolean;
  can_keep_password: boolean;
  missing: string[];
}

function SettingsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data, reload } = useAction<MailSettings>('marketing.settings');
  const [f, setF] = useState<MailSettings | null>(null);
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [testTo, setTestTo] = useState('');
  const [tried, setTried] = useState(false);
  const loaded = useRef(false);
  useEffect(() => {
    if (!data || loaded.current) return;
    loaded.current = true;
    setF({ ...data, site_url: data.site_url ?? window.location.origin });
  }, [data]);
  if (!f || !data) return <Skeleton h={400} />;
  const set = <K extends keyof MailSettings>(k: K, v: MailSettings[K]) => setF({ ...f, [k]: v });
  const errors = {
    host: !f.host?.trim() ? t('mkt.e_host') : null,
    from_email: !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.from_email ?? '') ? t('mkt.e_email') : null,
    address: (f.address ?? '').trim().length < 5 ? t('mkt.e_address') : null,
    site_url: !/^https?:\/\/[a-z0-9.-]+(:\d+)?$/i.test(f.site_url ?? '') ? t('mkt.e_site') : null,
    password: f.username && !data.has_password && !password ? t('mkt.e_password') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      const out = await action<MailSettings>('marketing.save_settings', {
        host: f.host,
        port: f.port,
        security: f.security,
        username: f.username || null,
        ...(password !== undefined ? { password: password || null } : {}),
        from_name: f.from_name || null,
        from_email: f.from_email,
        reply_to: f.reply_to || null,
        address: f.address,
        site_url: f.site_url,
        accent: f.accent,
        per_minute: f.per_minute,
        per_day: f.per_day,
      });
      setPassword(undefined);
      setF({ ...out, site_url: out.site_url ?? '' });
      await reload();
      await onChange();
    }, t('mkt.settings_saved'));
  };
  const err = (e: string | null) => (tried ? e : null);
  return (
    <div className="form-stack">
      {data.verified_at ? (
        <p className="callout callout-info">
          <Icon name="checkCircle" size={16} />
          <span>{t('mkt.verified', { at: dateTime(data.verified_at) })}</span>
        </p>
      ) : data.last_error ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} />
          <span>{t('mkt.server_error', { error: data.last_error })}</span>
        </p>
      ) : null}
      {!data.can_keep_password ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} />
          <span>{t('mkt.no_key')}</span>
        </p>
      ) : data.has_password && !data.password_readable ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} />
          <span>{t('mkt.password_lost')}</span>
        </p>
      ) : null}
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('mkt.server')}</h2>
          <p className="muted small">{t('mkt.server_hint')}</p>
        </div>
        <div className="form-grid">
          <Field label={t('mkt.host')} required error={err(errors.host)} hint={t('mkt.host_hint')}>
            <input className="input" value={f.host ?? ''} autoComplete="off" spellCheck={false} placeholder="mail.example.com" onChange={(e) => set('host', e.target.value.trim())} />
          </Field>
          <Field label={t('mkt.security')}>
            <select
              className="input"
              value={f.security}
              onChange={(e) => {
                const s = e.target.value as MailSettings['security'];
                setF({
                  ...f,
                  security: s,
                  port: s === 'ssl' ? 465 : s === 'starttls' ? 587 : 25,
                });
              }}
            >
              <option value="starttls">{t('mkt.sec_starttls')}</option>
              <option value="ssl">{t('mkt.sec_ssl')}</option>
              <option value="none">{t('mkt.sec_none')}</option>
            </select>
          </Field>
          <Field label={t('mkt.port')}>
            <input className="input" inputMode="numeric" value={f.port} onChange={(e) => set('port', Math.min(65535, Number(e.target.value.replace(/\D/g, '')) || 0))} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('mkt.username')} hint={t('mkt.username_hint')}>
            <input className="input" value={f.username ?? ''} autoComplete="off" spellCheck={false} onChange={(e) => set('username', e.target.value.trim() || null)} />
          </Field>
          <Field label={t('mkt.password')} error={err(errors.password)} hint={data.has_password && password === undefined ? t('mkt.password_kept') : t('mkt.password_hint')}>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={password ?? ''}
              placeholder={data.has_password && password === undefined ? '••••••••' : ''}
              disabled={!data.can_keep_password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        </div>
      </section>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('mkt.sender')}</h2>
          <p className="muted small">{t('mkt.sender_hint')}</p>
        </div>
        <div className="form-grid">
          <Field label={t('mkt.from_name')} hint={t('mkt.from_name_hint')}>
            <input className="input" value={f.from_name ?? ''} maxLength={80} onChange={(e) => set('from_name', e.target.value)} />
          </Field>
          <Field label={t('mkt.from_email')} required error={err(errors.from_email)}>
            <input className="input" type="email" value={f.from_email ?? ''} onChange={(e) => set('from_email', e.target.value.trim())} />
          </Field>
          <Field label={t('mkt.reply_to')} hint={t('mkt.optional')}>
            <input className="input" type="email" value={f.reply_to ?? ''} onChange={(e) => set('reply_to', e.target.value.trim() || null)} />
          </Field>
        </div>
        <Field label={t('mkt.address')} required error={err(errors.address)} hint={t('mkt.address_hint')}>
          <input className="input" value={f.address ?? ''} maxLength={200} onChange={(e) => set('address', e.target.value)} />
        </Field>
        <div className="form-grid">
          <Field label={t('mkt.site_url')} required error={err(errors.site_url)} hint={t('mkt.site_url_hint')}>
            <input className="input" value={f.site_url ?? ''} spellCheck={false} onChange={(e) => set('site_url', e.target.value.trim().replace(/\/+$/, ''))} />
          </Field>
          <Field label={t('mkt.accent')}>
            <span className="mkt-color">
              <input type="color" aria-label={t('mkt.accent')} value={f.accent} onChange={(e) => set('accent', e.target.value)} />
              <span className="tabular muted">{f.accent}</span>
            </span>
          </Field>
        </div>
      </section>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('mkt.pace')}</h2>
          <p className="muted small">{t('mkt.pace_hint')}</p>
        </div>
        <div className="form-grid">
          <NumberField label={t('mkt.per_minute')} value={f.per_minute} set={(n) => set('per_minute', Math.max(1, Math.min(600, n ?? 1)))} unit={t('mkt.emails')} />
          <Field label={t('mkt.per_day')} hint={t('mkt.per_day_hint')}>
            <span className="input-unit">
              <input className="input" inputMode="numeric" value={f.per_day} onChange={(e) => set('per_day', Math.max(1, Math.min(100000, Number(e.target.value.replace(/\D/g, '')) || 1)))} />
              <span>{t('mkt.emails')}</span>
            </span>
          </Field>
        </div>
      </section>
      <div className="row-actions mkt-settings-foot">
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {t('common.save')}
        </Button>
      </div>
      <section className="panel form-stack">
        <div>
          <h2 className="rep-sub">{t('mkt.check')}</h2>
          <p className="muted small">{t('mkt.check_hint')}</p>
        </div>
        <div className="mkt-check">
          <input className="input" type="email" value={testTo} placeholder={'you@example.com'} aria-label={t('mkt.test_to')} onChange={(e) => setTestTo(e.target.value)} />
          <Button
            icon="send"
            busy={busy}
            disabled={!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(testTo.trim()) || data.missing.length > 0}
            onClick={() =>
              void run(async () => {
                await action('marketing.verify', { to: testTo.trim() });
                await reload();
                await onChange();
              }, t('mkt.check_sent'))
            }
          >
            {t('mkt.check_send')}
          </Button>
        </div>
        {data.missing.length ? <p className="muted small">{t('mkt.save_first')}</p> : null}
      </section>
    </div>
  );
}
